import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Button,
  Stack,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Pagination,
  Dialog,
  DialogContent,
  IconButton,
  Tooltip,
  Divider,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FlagIcon from '@mui/icons-material/Flag';
import EventIcon from '@mui/icons-material/Event';
import DateRangeIcon from '@mui/icons-material/DateRange';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import { useNavigate } from 'react-router-dom';
import ProjectNavButtons from '../components/ProjectNavButtons';
import PageSearchBar from '../components/PageSearchBar';
import ProjectFiltersDialog from '../components/ProjectFiltersDialog';
import { buildPeSearchPageHref, countPeFilters } from '../utils/searchState';
import { useAppliedFilterChips } from '../hooks/useAppliedFilterChips';
import { usePeFilters } from '../hooks/usePeFilters';
import { usePeSearchDropdown } from '../hooks/usePeSearchDropdown';
import {
  projectEssentialApi,
  type ProjectActivityItem,
} from '../services/api';
import { PaginatedPosterGrid } from '../components/PaginatedPosterGrid';
import { EventPosterCard } from '../components/EventPosterCard';
import eventPlaceholderImg from '../../img/project_essential_event_placeholder.png';

function getNextWeekRange() {
  const today = new Date();
  const currentDay = today.getDay();
  const daysToNextMonday = currentDay === 0 ? 1 : 8 - currentDay;

  const nextMonday = new Date(today);
  nextMonday.setDate(today.getDate() + daysToNextMonday);
  nextMonday.setHours(0, 0, 0, 0);

  const nextSunday = new Date(nextMonday);
  nextSunday.setDate(nextMonday.getDate() + 6);
  nextSunday.setHours(23, 59, 59, 999);

  return { start: nextMonday, end: nextSunday };
}

function getNextNextWeekRange() {
  const { start: nextMonday } = getNextWeekRange();
  const nextNextMonday = new Date(nextMonday);
  nextNextMonday.setDate(nextMonday.getDate() + 7);

  const nextNextSunday = new Date(nextNextMonday);
  nextNextSunday.setDate(nextNextMonday.getDate() + 6);
  nextNextSunday.setHours(23, 59, 59, 999);

  return { start: nextNextMonday, end: nextNextSunday };
}

function isMultiDayEvent(start: string | null, end: string | null): boolean {
  if (!start || !end) return false;
  const dStart = new Date(start);
  const dEnd = new Date(end);
  const startDay = new Date(dStart.getFullYear(), dStart.getMonth(), dStart.getDate());
  const endDay = new Date(dEnd.getFullYear(), dEnd.getMonth(), dEnd.getDate());
  return startDay.getTime() !== endDay.getTime();
}

function getMultiDayEventsForWeek(week: Date[], activities: ProjectActivityItem[]) {
  const weekStart = new Date(week[0]);
  weekStart.setHours(0, 0, 0, 0);
  const weekEnd = new Date(week[6]);
  weekEnd.setHours(23, 59, 59, 999);

  return activities.filter((evt) => {
    if (!evt.start || !evt.end) return false;
    const isMulti = isMultiDayEvent(evt.start, evt.end);
    if (!isMulti) return false;

    const eStart = new Date(evt.start);
    const eEnd = new Date(evt.end);

    return eStart <= weekEnd && eEnd >= weekStart;
  });
}

function getEventsForWeek(week: Date[], activities: ProjectActivityItem[]) {
  const weekStart = new Date(week[0]);
  weekStart.setHours(0, 0, 0, 0);
  const weekEnd = new Date(week[6]);
  weekEnd.setHours(23, 59, 59, 999);

  return activities.filter((evt) => {
    if (!evt.start) return false;
    const eStart = new Date(evt.start);
    const eEnd = evt.end ? new Date(evt.end) : eStart;

    return eStart <= weekEnd && eEnd >= weekStart;
  });
}

interface PackedEvent {
  event: ProjectActivityItem;
  startCol: number;
  endCol: number;
  gridRow: number;
}

function packEventsForWeek(week: Date[], activities: ProjectActivityItem[]): PackedEvent[] {
  const weekEvents = getEventsForWeek(week, activities);

  // 1. Map events to column ranges
  const mapped = weekEvents.map((evt) => {
    const eStart = new Date(evt.start!);
    const eStartDay = new Date(eStart.getFullYear(), eStart.getMonth(), eStart.getDate());

    let startCol = 0;
    for (let i = 0; i < 7; i++) {
      const day = new Date(week[i]);
      const weekDay = new Date(day.getFullYear(), day.getMonth(), day.getDate());
      if (eStartDay.getTime() <= weekDay.getTime()) {
        startCol = i;
        break;
      }
    }

    const eEnd = evt.end ? new Date(evt.end) : eStart;
    const eEndDay = new Date(eEnd.getFullYear(), eEnd.getMonth(), eEnd.getDate());

    let endCol = startCol;
    if (evt.end) {
      endCol = 6;
      for (let i = 6; i >= startCol; i--) {
        const day = new Date(week[i]);
        const weekDay = new Date(day.getFullYear(), day.getMonth(), day.getDate());
        if (eEndDay.getTime() >= weekDay.getTime()) {
          endCol = i;
          break;
        }
      }
    }

    return {
      event: evt,
      startCol,
      endCol,
    };
  });

  // 2. Sort events: first by startCol ascending, then by column span descending
  mapped.sort((a, b) => {
    if (a.startCol !== b.startCol) {
      return a.startCol - b.startCol;
    }
    const spanA = a.endCol - a.startCol;
    const spanB = b.endCol - b.startCol;
    return spanB - spanA;
  });

  // 3. Greedy packing into rows
  const rows: boolean[][] = [];
  const packed: PackedEvent[] = [];

  for (const item of mapped) {
    let placedRowIdx = -1;

    for (let r = 0; r < rows.length; r++) {
      let isFree = true;
      for (let col = item.startCol; col <= item.endCol; col++) {
        if (rows[r][col]) {
          isFree = false;
          break;
        }
      }

      if (isFree) {
        placedRowIdx = r;
        break;
      }
    }

    if (placedRowIdx === -1) {
      rows.push(new Array(7).fill(false));
      placedRowIdx = rows.length - 1;
    }

    for (let col = item.startCol; col <= item.endCol; col++) {
      rows[placedRowIdx][col] = true;
    }

    packed.push({
      event: item.event,
      startCol: item.startCol,
      endCol: item.endCol,
      gridRow: placedRowIdx + 1,
    });
  }

  return packed;
}

function getOrdinalSuffix(day: number): string {
  if (day >= 11 && day <= 13) {
    return `${day}th`;
  }
  switch (day % 10) {
    case 1:  return `${day}st`;
    case 2:  return `${day}nd`;
    case 3:  return `${day}rd`;
    default: return `${day}th`;
  }
}

/** Early = days 1–10, Mid = 11–20, Late = 21–end. */
function monthThirdLabel(day: number): 'Early' | 'Mid' | 'Late' {
  if (day <= 10) return 'Early';
  if (day <= 20) return 'Mid';
  return 'Late';
}

/**
 * Important-dates label: "Mid Sep", "Early–Late Sep", "Late Aug – Early Sep".
 * Uses Early / Mid / Late thirds of the month instead of numeric day ranges.
 */
function formatImportantDateRange(startStr: string, endStr: string): string {
  const start = new Date(startStr);
  const end = new Date(endStr);

  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const startMonth = months[start.getMonth()];
  const endMonth = months[end.getMonth()];
  const startThird = monthThirdLabel(start.getDate());
  const endThird = monthThirdLabel(end.getDate());

  if (start.getFullYear() !== end.getFullYear()) {
    return `${startThird} ${startMonth} ${start.getFullYear()} – ${endThird} ${endMonth} ${end.getFullYear()}`;
  }

  if (startMonth !== endMonth) {
    return `${startThird} ${startMonth} – ${endThird} ${endMonth}`;
  }

  if (startThird === endThird) {
    return `${startThird} ${startMonth}`;
  }

  return `${startThird}–${endThird} ${startMonth}`;
}

function AccordionHeaderTitle({
  icon,
  children,
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--color-accent)',
          lineHeight: 0,
        }}
      >
        {icon}
      </Box>
      <Typography variant="h6" sx={{ fontWeight: 800, color: 'var(--color-text-strong)' }}>
        {children}
      </Typography>
    </Stack>
  );
}

function formatDateRangeHeader(prefix: string, start: Date, end: Date): string {
  const startDay = start.getDate();
  const startDayOrdinal = getOrdinalSuffix(startDay);
  const endDay = end.getDate();
  const endDayOrdinal = getOrdinalSuffix(endDay);

  if (start.getMonth() === end.getMonth()) {
    const monthStr = start.toLocaleString('en-US', { month: 'long' }); // e.g. "July"
    return `${prefix} (${startDayOrdinal} - ${endDayOrdinal} ${monthStr})`;
  } else {
    const startMonthStr = start.toLocaleString('en-US', { month: 'short' });
    const endMonthStr = end.toLocaleString('en-US', { month: 'short' });
    return `${prefix} (${startDayOrdinal} ${startMonthStr} - ${endDayOrdinal} ${endMonthStr})`;
  }
}

function formatEventDateTime(startStr: string | null, endStr: string | null) {
  if (!startStr) return { dateStr: '-', timeStr: '-' };
  const start = new Date(startStr);

  const pad = (n: number) => String(n).padStart(2, '0');
  const formatTime = (d: Date) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  const formatDate = (d: Date) => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
  };

  const startDateStr = formatDate(start);
  const startTimeStr = formatTime(start);

  if (!endStr) {
    return {
      dateStr: startDateStr,
      timeStr: startTimeStr,
    };
  }

  const end = new Date(endStr);
  const endDateStr = formatDate(end);
  const endTimeStr = formatTime(end);

  if (startDateStr === endDateStr) {
    return {
      dateStr: startDateStr,
      timeStr: `${startTimeStr} - ${endTimeStr}`,
    };
  } else {
    return {
      dateStr: `${startDateStr} - ${endDateStr}`,
      timeStr: `${startTimeStr} - ${endTimeStr}`,
    };
  }
}

function formatHtmlDescription(html: string): string {
  if (!html) return '';
  
  // Replace "RSVP via" with red bold "(RSVP required)", leaving colons intact
  let formatted = html.replace(/RSVP\s+via/gi, '<b style="color: #ff3b30; font-weight: 700;">(RSVP required)</b>');

  // 2. Format existing <a> tags
  formatted = formatted.replace(/<a\s+([^>]*?)>(.*?)<\/a>/gi, (_match, attrs) => {
    let newAttrs = attrs;
    if (!/target=/i.test(newAttrs)) {
      newAttrs += ' target="_blank"';
    }
    if (!/rel=/i.test(newAttrs)) {
      newAttrs += ' rel="noopener noreferrer"';
    }
    return `<a ${newAttrs} style="color: var(--color-accent); font-weight: 700; text-decoration: underline;">Open Link</a>`;
  });

  // 3. Format raw URLs (starting with http/https) not part of existing tags
  formatted = formatted.replace(/(href=")?(https?:\/\/[^\s<]+)/gi, (match, hrefPrefix, url) => {
    if (hrefPrefix) {
      return match;
    }
    return `<a href="${url}" target="_blank" rel="noopener noreferrer" style="color: var(--color-accent); font-weight: 700; text-decoration: underline;">Open Link</a>`;
  });

  return formatted;
}

function EventRow({ event, onClick }: { event: ProjectActivityItem; onClick?: () => void }) {
  const { dateStr, timeStr } = formatEventDateTime(event.start, event.end);

  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'flex-start', sm: 'center' },
        gap: { xs: 1.5, sm: 2 },
        p: { xs: 1.5, sm: 2 },
        borderRadius: 2,
        border: '1px solid var(--color-border-strong)',
        bgcolor: 'var(--color-surface)',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'border-color 120ms ease, box-shadow 120ms ease',
        '&:hover': {
          borderColor: 'var(--color-accent)',
          boxShadow: '0 0 0 1px var(--color-accent)',
        },
      }}
    >
      {/* Thumbnail with overlaid Date Badge on the left */}
      <Box
        sx={{
          position: 'relative',
          width: { xs: '100%', sm: 100 },
          height: { xs: 'auto', sm: 100 },
          aspectRatio: { xs: '16 / 9', sm: 'unset' },
          borderRadius: 2,
          overflow: 'hidden',
          border: '1px solid var(--color-border-strong)',
          flexShrink: 0,
        }}
      >
        <Box
          component="img"
          src={event.thumbnail || eventPlaceholderImg}
          alt={event.name}
          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
        {/* Overlaid Date Badge */}
        <Box
          sx={{
            position: 'absolute',
            top: 6,
            left: 6,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: 38,
            height: 38,
            borderRadius: 1,
            bgcolor: 'rgba(255, 255, 255, 0.95)',
            border: '1px solid var(--color-border-strong)',
            boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
            zIndex: 2,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontWeight: 800,
              color: 'var(--color-accent)',
              textTransform: 'uppercase',
              fontSize: '0.55rem',
              lineHeight: 1,
            }}
          >
            {event.start ? new Date(event.start).toLocaleString('en-US', { month: 'short' }) : 'EVT'}
          </Typography>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 950,
              color: '#0A2540',
              lineHeight: 1,
              mt: 0.1,
              fontSize: '0.75rem',
            }}
          >
            {event.start ? new Date(event.start).getDate() : '-'}
          </Typography>
        </Box>
      </Box>

      {/* Details in the middle */}
      <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 700,
            color: 'var(--color-text-strong)',
            lineHeight: 1.3,
            mb: 1,
          }}
        >
          {event.name}
        </Typography>

        {/* Event Metadata (Vertical Stack) */}
        <Stack spacing={0.75} sx={{ mb: 1.5 }}>
          {/* Date */}
          <Stack direction="row" spacing={1} alignItems="center">
            <CalendarMonthIcon sx={{ fontSize: '1rem', color: 'var(--color-accent)' }} />
            <Typography
              variant="caption"
              sx={{
                color: 'var(--color-text-muted)',
                fontWeight: 600,
                fontSize: '0.8rem',
              }}
            >
              {dateStr}
            </Typography>
          </Stack>

          {/* Time */}
          <Stack direction="row" spacing={1} alignItems="center">
            <AccessTimeIcon sx={{ fontSize: '1rem', color: 'var(--color-accent)' }} />
            <Typography
              variant="caption"
              sx={{
                color: 'var(--color-text-muted)',
                fontWeight: 600,
                fontSize: '0.8rem',
              }}
            >
              {timeStr}
            </Typography>
          </Stack>

          {/* Location */}
          {event.location && (
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <LocationOnIcon sx={{ fontSize: '1rem', color: 'var(--color-accent)', mt: 0.2 }} />
              <Typography
                variant="caption"
                component="span"
                dangerouslySetInnerHTML={{ __html: formatHtmlDescription(event.location) }}
                sx={{
                  color: 'var(--color-text-muted)',
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  '& a': {
                    color: 'var(--color-accent)',
                    fontWeight: 700,
                    textDecoration: 'underline',
                    '&:hover': {
                      color: 'var(--color-accent-strong)',
                    },
                  },
                }}
              />
            </Stack>
          )}
        </Stack>

        {event.description && (
          <Typography
            variant="body2"
            component="div"
            dangerouslySetInnerHTML={{ __html: formatHtmlDescription(event.description) }}
            sx={{
              color: 'var(--color-text-muted)',
              fontSize: '0.85rem',
              lineHeight: 1.4,
              '& a': {
                color: 'var(--color-accent)',
                fontWeight: 700,
                textDecoration: 'underline',
                '&:hover': {
                  color: 'var(--color-accent-strong)',
                },
              },
            }}
          />
        )}
      </Box>

      {/* Add to Calendar button on the right */}
      <Box sx={{ alignSelf: { xs: 'stretch', sm: 'center' }, mt: { xs: 1, sm: 0 }, flexShrink: 0 }}>
        <Button
          component="a"
          href={buildGoogleCalendarUrl(event)}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()} // Stop propagation so it doesn't trigger row click!
          variant="outlined"
          size="small"
          startIcon={<CalendarMonthIcon />}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: 999,
            borderColor: 'var(--color-border-strong)',
            color: 'var(--color-text-strong)',
            fontSize: '0.75rem',
            width: '100%',
            whiteSpace: 'nowrap',
            px: 2,
            '&:hover': {
              borderColor: 'var(--color-accent)',
              bgcolor: 'var(--color-accent-soft)',
            },
          }}
        >
          Add to Calendar
        </Button>
      </Box>
    </Box>
  );
}


function formatCalendarEventTime(startStr: string | null, endStr: string | null): string {
  if (!startStr) return '';
  const start = new Date(startStr);
  
  const formatSingleTime = (d: Date) => {
    let hours = d.getHours();
    const minutes = d.getMinutes();
    const ampm = hours >= 12 ? 'pm' : 'am';
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    const minStr = minutes > 0 ? `:${String(minutes).padStart(2, '0')}` : '';
    return `${hours}${minStr}${ampm}`;
  };

  const startTimeFormatted = formatSingleTime(start);
  
  if (endStr) {
    const end = new Date(endStr);
    const isSameDay = start.toDateString() === end.toDateString();
    const endTimeFormatted = formatSingleTime(end);
    if (isSameDay) {
      return `${startTimeFormatted}-${endTimeFormatted}`;
    } else {
      return `Fr ${startTimeFormatted}`;
    }
  }
  
  return startTimeFormatted;
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const WEEK_DAYS = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];

function getCalendarGridDays(year: number, month: number): Date[] {
  const firstDay = new Date(year, month, 1);
  const firstDayOfWeek = firstDay.getDay(); // 0 (Sunday) to 6 (Saturday)
  const padDaysCount = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;

  const days: Date[] = [];
  const startDate = new Date(firstDay);
  startDate.setDate(firstDay.getDate() - padDaysCount);

  for (let i = 0; i < 42; i++) {
    const d = new Date(startDate);
    d.setDate(startDate.getDate() + i);
    days.push(d);
  }
  return days;
}

function formatGoogleCalendarDate(isoStr: string | null): string {
  if (!isoStr) return '';
  try {
    const d = new Date(isoStr);
    if (isNaN(d.getTime())) return '';
    const pad = (n: number) => String(n).padStart(2, '0');
    const yyyy = d.getUTCFullYear();
    const mm = pad(d.getUTCMonth() + 1);
    const dd = pad(d.getUTCDate());
    const hh = pad(d.getUTCHours());
    const min = pad(d.getUTCMinutes());
    const ss = pad(d.getUTCSeconds());
    return `${yyyy}${mm}${dd}T${hh}${min}${ss}Z`;
  } catch {
    return '';
  }
}

function buildGoogleCalendarUrl(event: ProjectActivityItem): string {
  if (!event.start) return '#';
  
  const startFormatted = formatGoogleCalendarDate(event.start);
  let endFormatted = formatGoogleCalendarDate(event.end);
  
  if (!endFormatted) {
    const startDate = new Date(event.start);
    const endDate = new Date(startDate.getTime() + 60 * 60 * 1000);
    endFormatted = formatGoogleCalendarDate(endDate.toISOString());
  }
  
  const dates = `${startFormatted}/${endFormatted}`;
  
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    dates,
    text: event.name || '',
    location: event.location || '',
    details: event.description || '',
  });
  
  return `https://www.google.com/calendar/event?${params.toString()}`;
}

export default function ProjectCalendarPage() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Universal search launcher with live dropdown suggestions.
  const [filters, setFilters] = usePeFilters();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const filterCount = countPeFilters(filters);
  const { chips: appliedFilterChips, clearAll: clearAllFilters } = useAppliedFilterChips(
    filters,
    setFilters,
  );
  const { search, setSearch, dismissDropdownKey, dropdownContent } = usePeSearchDropdown();

  const [activities, setActivities] = useState<ProjectActivityItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [upcomingPage, setUpcomingPage] = useState(1);

  const [importantDatesExpanded, setImportantDatesExpanded] = useState(true);
  const [upcomingExpanded, setUpcomingExpanded] = useState(true);
  const [nextWeekExpanded, setNextWeekExpanded] = useState(false);
  const [nextNextWeekExpanded, setNextNextWeekExpanded] = useState(false);

  const [calendarOpen, setCalendarOpen] = useState(false);
  const [currentYear, setCurrentYear] = useState(() => new Date().getFullYear());
  const [currentMonth, setCurrentMonth] = useState(() => new Date().getMonth());
  const [selectedEvent, setSelectedEvent] = useState<ProjectActivityItem | null>(null);
  // Ref on today's row inside the calendar dialog so we can auto-scroll
  // to it whenever the dialog opens on the current month.
  const todayRowRef = React.useRef<HTMLDivElement | null>(null);

  // Auto-scroll to today whenever the calendar dialog opens on the
  // current real-world month. Small delay so the dialog transition
  // finishes and the target row is measurable.
  useEffect(() => {
    if (!calendarOpen) return;
    const now = new Date();
    if (currentMonth !== now.getMonth() || currentYear !== now.getFullYear()) return;
    const timer = window.setTimeout(() => {
      todayRowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 220);
    return () => window.clearTimeout(timer);
  }, [calendarOpen, currentMonth, currentYear, isMobile, activities]);

  useEffect(() => {
    setLoading(true);
    projectEssentialApi.getProjectActivities()
      .then((res: { items: ProjectActivityItem[] }) => {
        setActivities(res.items);
        setLoading(false);
      })
      .catch((err: any) => {
        console.error('Failed to load project activities:', err);
        setError('Failed to load events. Please try again later.');
        setLoading(false);
      });
  }, []);

  const gridDays = useMemo(() => getCalendarGridDays(currentYear, currentMonth), [currentYear, currentMonth]);

  const weeks = useMemo(() => {
    const w: Date[][] = [];
    for (let i = 0; i < gridDays.length; i += 7) {
      const week = gridDays.slice(i, i + 7);
      const hasCurrentMonthDay = week.some((date) => date.getMonth() === currentMonth);
      if (hasCurrentMonthDay) {
        w.push(week);
      }
    }
    return w;
  }, [gridDays, currentMonth]);

  const getEventsForDate = (date: Date) => {
    const targetDateStr = date.toDateString();
    return activities.filter((event) => {
      if (!event.start) return false;
      if (isMultiDayEvent(event.start, event.end)) return false;
      const eventDate = new Date(event.start);
      return eventDate.toDateString() === targetDateStr;
    });
  };

  const handlePrevMonth = () => {
    setCurrentMonth((prev) => {
      if (prev === 0) {
        setCurrentYear((y) => y - 1);
        return 11;
      }
      return prev - 1;
    });
  };

  const handleNextMonth = () => {
    setCurrentMonth((prev) => {
      if (prev === 11) {
        setCurrentYear((y) => y + 1);
        return 0;
      }
      return prev + 1;
    });
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Important Dates: multi-day events containing 'sf preview' or 'bookings' case-insensitive
  const importantDates = activities
    .filter((act) => {
      if (!act.start || !act.end) return false;
      const isMulti = isMultiDayEvent(act.start, act.end);
      if (!isMulti) return false;
      
      const nameLower = (act.name || '').toLowerCase();
      return nameLower.includes('sf preview') || nameLower.includes('booking');
    })
    .sort((a, b) => {
      const dateA = a.start ? new Date(a.start).getTime() : 0;
      const dateB = b.start ? new Date(b.start).getTime() : 0;
      return dateA - dateB;
    });

  // 1. Upcoming Events: Nearest 20 upcoming events from today onwards
  const futureActivities = activities.filter((act) => {
    if (!act.start) return false;
    return new Date(act.start) >= today;
  });
  const upcomingEvents = futureActivities.slice(0, 20);

  const upcomingPageSize = 5;
  const upcomingPageCount = Math.ceil(upcomingEvents.length / upcomingPageSize);
  const safeUpcomingPage = Math.min(upcomingPage, upcomingPageCount || 1);
  const slicedUpcomingEvents = upcomingEvents.slice(
    (safeUpcomingPage - 1) * upcomingPageSize,
    safeUpcomingPage * upcomingPageSize,
  );

  // 2. Next week's events
  const nextWeekRange = getNextWeekRange();
  const nextWeekEvents = activities
    .filter((act) => {
      if (!act.start) return false;
      const date = new Date(act.start);
      return date >= nextWeekRange.start && date <= nextWeekRange.end;
    })
    .sort((a, b) => {
      const aMulti = isMultiDayEvent(a.start, a.end);
      const bMulti = isMultiDayEvent(b.start, b.end);
      if (aMulti && !bMulti) return -1;
      if (!aMulti && bMulti) return 1;
      const dateA = a.start ? new Date(a.start).getTime() : 0;
      const dateB = b.start ? new Date(b.start).getTime() : 0;
      return dateA - dateB;
    });
  const nextWeekHeader = formatDateRangeHeader('Next Week', nextWeekRange.start, nextWeekRange.end);

  // 3. Next next week's events
  const nextNextWeekRange = getNextNextWeekRange();
  const nextNextWeekEvents = activities
    .filter((act) => {
      if (!act.start) return false;
      const date = new Date(act.start);
      return date >= nextNextWeekRange.start && date <= nextNextWeekRange.end;
    })
    .sort((a, b) => {
      const aMulti = isMultiDayEvent(a.start, a.end);
      const bMulti = isMultiDayEvent(b.start, b.end);
      if (aMulti && !bMulti) return -1;
      if (!aMulti && bMulti) return 1;
      const dateA = a.start ? new Date(a.start).getTime() : 0;
      const dateB = b.start ? new Date(b.start).getTime() : 0;
      return dateA - dateB;
    });
  const nextNextWeekHeader = formatDateRangeHeader('Following Week', nextNextWeekRange.start, nextNextWeekRange.end);

  if (loading) {
    return (
      <Stack spacing={3} alignItems="center" justifyContent="center" sx={{ py: 10 }}>
        <CircularProgress sx={{ color: 'var(--color-accent)' }} />
        <Typography variant="body2" sx={{ color: 'var(--color-text-muted)' }}>
          Loading calendar activities...
        </Typography>
      </Stack>
    );
  }

  if (error) {
    return (
      <Stack spacing={2} alignItems="center" justifyContent="center" sx={{ py: 10, px: 2 }}>
        <Typography variant="body1" color="error" sx={{ fontWeight: 600 }}>
          {error}
        </Typography>
        <Button
          onClick={() => navigate('/project-essential')}
          variant="contained"
          size="small"
          sx={{ bgcolor: 'var(--color-accent)', textTransform: 'none' }}
        >
          Go Back
        </Button>
      </Stack>
    );
  }

  return (
    <Stack spacing={{ xs: 2, sm: 2.5, md: 3 }} sx={{ pb: 6 }}>
      {/* Back to dashboard */}
      <Box>
        <Button
          onClick={() => navigate('/project-essential')}
          startIcon={<ArrowBackIcon />}
          variant="outlined"
          size="small"
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 999,
            borderColor: 'var(--color-border-strong)',
            color: 'var(--color-text-strong)',
            bgcolor: 'var(--color-surface)',
            '&:hover': {
              borderColor: 'var(--color-accent)',
              bgcolor: 'var(--color-accent-soft)',
            },
          }}
        >
          Back to Dashboard
        </Button>
      </Box>

      <Typography
        variant="h4"
        sx={{
          fontWeight: 800,
          color: 'var(--color-accent)',
          textAlign: 'center',
          letterSpacing: '0.01em',
          fontSize: { xs: '1.6rem', sm: '2rem', md: '2.25rem' },
        }}
      >
        Project Calendar
      </Typography>

      <PageSearchBar
        value={search}
        onChange={setSearch}
        placeholder="Search projects, EDMs, eBooks and reports…"
        onSubmit={(query) => navigate(buildPeSearchPageHref(query, filters))}
        filterCount={filterCount}
        onOpenFilters={() => setFiltersOpen(true)}
        appliedFilters={appliedFilterChips}
        onClearAllFilters={clearAllFilters}
        dropdownContent={dropdownContent}
        dismissDropdownKey={dismissDropdownKey}
      />

      <ProjectNavButtons active="calendar" />

      {/* View / Subscribe — single action row */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        sx={{ width: '100%', mt: 1, mb: 1 }}
      >
        <Button
          fullWidth
          variant="contained"
          startIcon={<CalendarMonthIcon />}
          onClick={() => setCalendarOpen(true)}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            py: 1.15,
            borderRadius: 2,
            bgcolor: 'var(--color-accent)',
            color: '#fff',
            fontSize: '0.95rem',
            boxShadow: 'none',
            '&:hover': {
              bgcolor: 'var(--color-accent-strong)',
              boxShadow: 'none',
            },
          }}
        >
          View Calendar
        </Button>
        <Button
          component="a"
          href="https://portal.huttonsgroup.com/Project/CalendarSubscribe"
          target="_blank"
          rel="noopener noreferrer"
          fullWidth
          variant="contained"
          disableElevation
          startIcon={
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 28,
                height: 28,
                borderRadius: '50%',
                bgcolor: 'rgba(255,255,255,0.22)',
                flexShrink: 0,
              }}
            >
              <NotificationsActiveIcon
                sx={{
                  fontSize: 18,
                  animation: 'pe-bell-ring 4.5s ease-in-out infinite',
                  transformOrigin: 'top center',
                  '@keyframes pe-bell-ring': {
                    '0%, 72%, 100%': { transform: 'rotate(0deg)' },
                    '76%': { transform: 'rotate(18deg)' },
                    '80%': { transform: 'rotate(-16deg)' },
                    '84%': { transform: 'rotate(14deg)' },
                    '88%': { transform: 'rotate(-12deg)' },
                    '92%': { transform: 'rotate(8deg)' },
                    '96%': { transform: 'rotate(-4deg)' },
                  },
                }}
              />
            </Box>
          }
          sx={{
            position: 'relative',
            overflow: 'visible',
            textTransform: 'none',
            fontWeight: 800,
            py: 1.15,
            borderRadius: 2,
            border: '3px solid #0f766e',
            bgcolor: '#0d9488',
            color: '#fff',
            fontSize: '0.95rem',
            letterSpacing: '0.02em',
            // Teal is intentional — keeps this distinct from theme accent
            // (orange in light/dark, navy in classic) so it doesn't blend
            // with View Calendar.
            boxShadow:
              '0 4px 14px rgba(13, 148, 136, 0.35), 0 0 0 0 rgba(13, 148, 136, 0.4)',
            animation: 'pe-subscribe-pulse 4.5s ease-in-out infinite',
            '@keyframes pe-subscribe-pulse': {
              '0%, 70%, 100%': {
                boxShadow:
                  '0 4px 14px rgba(13, 148, 136, 0.35), 0 0 0 0 rgba(13, 148, 136, 0)',
                transform: 'scale(1)',
              },
              '78%': {
                boxShadow:
                  '0 6px 18px rgba(13, 148, 136, 0.45), 0 0 0 8px rgba(13, 148, 136, 0.18)',
                transform: 'scale(1.02)',
              },
              '86%': {
                boxShadow:
                  '0 4px 14px rgba(13, 148, 136, 0.35), 0 0 0 0 rgba(13, 148, 136, 0)',
                transform: 'scale(1)',
              },
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              top: -5,
              right: -5,
              width: 12,
              height: 12,
              borderRadius: '50%',
              bgcolor: '#fbbf24',
              border: '2px solid #fff',
              boxShadow: '0 0 0 0 rgba(251, 191, 36, 0.55)',
              animation: 'pe-subscribe-dot 4.5s ease-in-out infinite',
              '@keyframes pe-subscribe-dot': {
                '0%, 70%, 100%': {
                  boxShadow: '0 0 0 0 rgba(251, 191, 36, 0)',
                  transform: 'scale(1)',
                },
                '78%': {
                  boxShadow: '0 0 0 6px rgba(251, 191, 36, 0)',
                  transform: 'scale(1.15)',
                },
                '86%': {
                  boxShadow: '0 0 0 0 rgba(251, 191, 36, 0)',
                  transform: 'scale(1)',
                },
              },
            },
            '&:hover': {
              bgcolor: '#0f766e',
              borderColor: '#115e59',
              color: '#fff',
              animation: 'none',
              transform: 'scale(1)',
              boxShadow: '0 6px 20px rgba(13, 148, 136, 0.5)',
              '& .MuiButton-startIcon svg': {
                animation: 'none',
              },
              '&::after': {
                animation: 'none',
              },
            },
          }}
        >
          Subscribe for Alerts
        </Button>
      </Stack>

      {/* Accordions */}
      <Box sx={{ width: '100%', mt: 2 }}>

        {/* Accordion 0: Important Dates */}
        <Accordion
          expanded={importantDatesExpanded}
          onChange={() => setImportantDatesExpanded(!importantDatesExpanded)}
          disableGutters
          elevation={0}
          sx={{
            border: '1px solid var(--color-border-strong)',
            borderRadius: '8px !important',
            overflow: 'hidden',
            mb: 2,
            bgcolor: 'var(--color-surface)',
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: 'var(--color-text-strong)' }} />}
            sx={{
              bgcolor: 'var(--color-surface-elevated)',
              borderBottom: importantDatesExpanded ? '1px solid var(--color-border-strong)' : 'none',
              px: { xs: 2, sm: 2.5 },
              py: 0.5,
            }}
          >
            <AccordionHeaderTitle icon={<FlagIcon fontSize="small" />}>
              Important Dates
            </AccordionHeaderTitle>
          </AccordionSummary>
          <AccordionDetails sx={{ p: { xs: 2, sm: 2.5 } }}>
            {importantDates.length === 0 ? (
              <Typography variant="body2" sx={{ color: 'var(--color-text-muted)', py: 2, textAlign: 'center' }}>
                No multi-day events scheduled.
              </Typography>
            ) : (
              <Stack spacing={1.25}>
                {importantDates.map((evt) => {
                  const dateRangeStr = formatImportantDateRange(evt.start!, evt.end!);
                  return (
                    <Box
                      key={evt.id}
                      onClick={() => setSelectedEvent(evt)}
                      sx={{
                        cursor: 'pointer',
                        py: 0.5,
                        px: 1,
                        borderRadius: 1.5,
                        display: 'flex',
                        alignItems: 'baseline',
                        gap: 1.25,
                        '&:hover': {
                          bgcolor: 'var(--color-surface-elevated)',
                        },
                        transition: 'background-color 150ms ease',
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 850,
                          color: 'var(--color-text-strong)',
                          fontSize: '0.925rem',
                          flexShrink: 0,
                          minWidth: '6.5rem',
                        }}
                      >
                        {dateRangeStr}:
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 700,
                          color: evt.colorCode || 'var(--color-accent)',
                          fontSize: '0.925rem',
                        }}
                      >
                        {evt.name}
                      </Typography>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </AccordionDetails>
        </Accordion>
        
        {/* Accordion 1: Upcoming Events */}
        <Accordion
          expanded={upcomingExpanded}
          onChange={() => setUpcomingExpanded(!upcomingExpanded)}
          disableGutters
          elevation={0}
          sx={{
            border: '1px solid var(--color-border-strong)',
            borderRadius: '8px !important',
            overflow: 'hidden',
            mb: 2,
            bgcolor: 'var(--color-surface)',
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: 'var(--color-text-strong)' }} />}
            sx={{
              bgcolor: 'var(--color-surface-elevated)',
              borderBottom: upcomingExpanded ? '1px solid var(--color-border-strong)' : 'none',
              px: { xs: 2, sm: 2.5 },
              py: 0.5,
            }}
          >
            <AccordionHeaderTitle icon={<EventIcon fontSize="small" />}>
              Upcoming Events
            </AccordionHeaderTitle>
          </AccordionSummary>
          <AccordionDetails sx={{ p: { xs: 1.5, sm: 2.5 } }}>
            {upcomingEvents.length === 0 ? (
              <Typography variant="body2" sx={{ color: 'var(--color-text-muted)', py: 2, textAlign: 'center' }}>
                No upcoming events to display.
              </Typography>
            ) : (
              <Stack spacing={2}>
                <Stack spacing={1.5}>
                  {slicedUpcomingEvents.map((event, index) => {
                    const currentEventDate = event.start ? new Date(event.start).toDateString() : '';
                    const nextEvent = slicedUpcomingEvents[index + 1];
                    const nextEventDate = nextEvent && nextEvent.start ? new Date(nextEvent.start).toDateString() : '';
                    const isDifferentDay = nextEvent && currentEventDate !== nextEventDate;

                    return (
                      <React.Fragment key={event.id}>
                        <EventRow event={event} onClick={() => setSelectedEvent(event)} />
                        {isDifferentDay && (
                          <Divider
                            sx={{
                              borderColor: 'var(--color-border-strong)',
                              borderWidth: '1px',
                              my: 1.5,
                            }}
                          />
                        )}
                      </React.Fragment>
                    );
                  })}
                </Stack>
                {upcomingPageCount > 1 && (
                  <Stack direction="row" justifyContent="center" sx={{ mt: 1 }}>
                    <Pagination
                      count={upcomingPageCount}
                      page={safeUpcomingPage}
                      onChange={(_e, p) => setUpcomingPage(p)}
                      shape="rounded"
                      siblingCount={1}
                      sx={{
                        '& .MuiPaginationItem-root': {
                          color: 'var(--color-text-strong)',
                          borderColor: 'var(--color-border-strong)',
                          fontWeight: 600,
                          '&.Mui-selected': {
                            bgcolor: 'var(--color-accent)',
                            color: '#fff',
                            '&:hover': { bgcolor: 'var(--color-accent-strong)' },
                          },
                          '&:hover': { bgcolor: 'var(--color-accent-soft)' },
                        },
                      }}
                    />
                  </Stack>
                )}
              </Stack>
            )}
          </AccordionDetails>
        </Accordion>

        {/* Accordion 2: Next Week */}
        <Accordion
          expanded={nextWeekExpanded}
          onChange={() => setNextWeekExpanded(!nextWeekExpanded)}
          disableGutters
          elevation={0}
          sx={{
            border: '1px solid var(--color-border-strong)',
            borderRadius: '8px !important',
            overflow: 'hidden',
            mb: 2,
            bgcolor: 'var(--color-surface)',
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: 'var(--color-text-strong)' }} />}
            sx={{
              bgcolor: 'var(--color-surface-elevated)',
              borderBottom: nextWeekExpanded ? '1px solid var(--color-border-strong)' : 'none',
              px: { xs: 2, sm: 2.5 },
              py: 0.5,
            }}
          >
            <AccordionHeaderTitle icon={<DateRangeIcon fontSize="small" />}>
              {nextWeekHeader}
            </AccordionHeaderTitle>
          </AccordionSummary>
          <AccordionDetails sx={{ p: { xs: 1.5, sm: 2.5 } }}>
            {nextWeekEvents.length === 0 ? (
              <Typography variant="body2" sx={{ color: 'var(--color-text-muted)', py: 4, textAlign: 'center' }}>
                No events scheduled for this week.
              </Typography>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  overflowX: 'auto',
                  overflowY: 'hidden',
                  gap: 2,
                  pb: 1.5,
                  WebkitOverflowScrolling: 'touch',
                  // Allow horizontal carousel drag and vertical page scroll.
                  touchAction: 'pan-x pan-y',
                  overscrollBehaviorX: 'contain',
                  '&::-webkit-scrollbar': {
                    height: '6px',
                  },
                  '&::-webkit-scrollbar-track': {
                    bgcolor: 'var(--color-surface-elevated)',
                    borderRadius: '3px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    bgcolor: 'var(--color-border-strong)',
                    borderRadius: '3px',
                    '&:hover': {
                      bgcolor: 'var(--color-accent-soft)',
                    },
                  },
                }}
              >
                {nextWeekEvents.map((event) => (
                  <Box
                    key={event.id}
                    sx={{
                      width: { xs: '260px', sm: '280px', md: '300px' },
                      flexShrink: 0,
                    }}
                  >
                    <EventPosterCard event={event} />
                  </Box>
                ))}
              </Box>
            )}
          </AccordionDetails>
        </Accordion>

        {/* Accordion 3: Next Next Week */}
        <Accordion
          expanded={nextNextWeekExpanded}
          onChange={() => setNextNextWeekExpanded(!nextNextWeekExpanded)}
          disableGutters
          elevation={0}
          sx={{
            border: '1px solid var(--color-border-strong)',
            borderRadius: '8px !important',
            overflow: 'hidden',
            bgcolor: 'var(--color-surface)',
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: 'var(--color-text-strong)' }} />}
            sx={{
              bgcolor: 'var(--color-surface-elevated)',
              borderBottom: nextNextWeekExpanded ? '1px solid var(--color-border-strong)' : 'none',
              px: { xs: 2, sm: 2.5 },
              py: 0.5,
            }}
          >
            <AccordionHeaderTitle icon={<EventRepeatIcon fontSize="small" />}>
              {nextNextWeekHeader}
            </AccordionHeaderTitle>
          </AccordionSummary>
          <AccordionDetails sx={{ p: { xs: 1.5, sm: 2.5 } }}>
            {nextNextWeekEvents.length === 0 ? (
              <Typography variant="body2" sx={{ color: 'var(--color-text-muted)', py: 4, textAlign: 'center' }}>
                No events scheduled for this week.
              </Typography>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  overflowX: 'auto',
                  overflowY: 'hidden',
                  gap: 2,
                  pb: 1.5,
                  WebkitOverflowScrolling: 'touch',
                  // Allow horizontal carousel drag and vertical page scroll.
                  touchAction: 'pan-x pan-y',
                  overscrollBehaviorX: 'contain',
                  '&::-webkit-scrollbar': {
                    height: '6px',
                  },
                  '&::-webkit-scrollbar-track': {
                    bgcolor: 'var(--color-surface-elevated)',
                    borderRadius: '3px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    bgcolor: 'var(--color-border-strong)',
                    borderRadius: '3px',
                    '&:hover': {
                      bgcolor: 'var(--color-accent-soft)',
                    },
                  },
                }}
              >
                {nextNextWeekEvents.map((event) => (
                  <Box
                    key={event.id}
                    sx={{
                      width: { xs: '260px', sm: '280px', md: '300px' },
                      flexShrink: 0,
                    }}
                  >
                    <EventPosterCard event={event} />
                  </Box>
                ))}
              </Box>
            )}
          </AccordionDetails>
        </Accordion>

      </Box>

      {/* Calendar Dialog Modal */}
      <Dialog
        open={calendarOpen}
        onClose={() => setCalendarOpen(false)}
        maxWidth="xl"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: 3 },
            overflow: 'hidden',
            bgcolor: 'var(--color-surface)',
            height: { xs: '100vh', sm: '90vh' },
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {/* Dialog Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, py: 2, borderBottom: '1px solid var(--color-border-strong)' }}>
          <Typography variant="h6" sx={{ fontWeight: 800, color: 'var(--color-text-strong)' }}>
            Project Calendar
          </Typography>
          <IconButton onClick={() => setCalendarOpen(false)} size="small" sx={{ color: 'var(--color-text-muted)' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Month Selector Bar */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            px: 3,
            py: 1.5,
            bgcolor: 'var(--color-surface-elevated)',
            borderBottom: '1px solid var(--color-border-strong)',
          }}
        >
          {/* Chevron controls */}
          <Stack direction="row" spacing={1} alignItems="center">
            <IconButton onClick={handlePrevMonth} size="small" sx={{ border: '1px solid var(--color-border-strong)' }}>
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <Typography variant="h6" sx={{ fontWeight: 850, color: 'var(--color-text-strong)', minWidth: 150, textAlign: 'center' }}>
              {MONTH_NAMES[currentMonth]} {currentYear}
            </Typography>
            <IconButton onClick={handleNextMonth} size="small" sx={{ border: '1px solid var(--color-border-strong)' }}>
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Stack>

          {/* Direct selects for jumps */}
          <Stack direction="row" spacing={1.5} alignItems="center">
            <FormControl size="small" variant="outlined">
              <Select
                value={currentMonth}
                onChange={(e) => setCurrentMonth(Number(e.target.value))}
                sx={{
                  borderRadius: 2,
                  fontWeight: 700,
                  fontSize: '0.85rem',
                  bgcolor: 'var(--color-surface)',
                  color: 'var(--color-text-strong)',
                }}
              >
                {MONTH_NAMES.map((name, idx) => (
                  <MenuItem key={name} value={idx} sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" variant="outlined">
              <Select
                value={currentYear}
                onChange={(e) => setCurrentYear(Number(e.target.value))}
                sx={{
                  borderRadius: 2,
                  fontWeight: 700,
                  fontSize: '0.85rem',
                  bgcolor: 'var(--color-surface)',
                  color: 'var(--color-text-strong)',
                }}
              >
                {[2024, 2025, 2026, 2027, 2028].map((yr) => (
                  <MenuItem key={yr} value={yr} sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                    {yr}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Box>

        {/* Calendar Body */}
        <DialogContent sx={{ p: 0, overflow: 'auto', flex: 1, bgcolor: 'var(--color-bg)' }}>
          {isMobile ? (
            // ── Mobile Agenda View ─────────────────────────────────────
            // 7-column week grid doesn't fit on phones; instead render a
            // vertical, day-by-day agenda list of every date in the
            // current month that has at least one event. Multi-day events
            // appear on each day they span so nothing is hidden. Tapping
            // an event opens the same detail dialog as desktop.
            (() => {
              const monthDays = gridDays.filter((d) => d.getMonth() === currentMonth);
              const daysWithEvents = monthDays
                .map((date) => {
                  const targetDateStr = date.toDateString();
                  const events = activities
                    .filter((evt) => {
                      if (!evt.start) return false;
                      const eStart = new Date(evt.start);
                      const eEnd = evt.end ? new Date(evt.end) : eStart;
                      const dayStart = new Date(date);
                      dayStart.setHours(0, 0, 0, 0);
                      const dayEnd = new Date(date);
                      dayEnd.setHours(23, 59, 59, 999);
                      return eStart <= dayEnd && eEnd >= dayStart;
                    })
                    .sort((a, b) => {
                      const ta = a.start ? new Date(a.start).getTime() : 0;
                      const tb = b.start ? new Date(b.start).getTime() : 0;
                      return ta - tb;
                    });
                  return { date, events };
                })
                .filter((d) => d.events.length > 0);

              if (daysWithEvents.length === 0) {
                return (
                  <Box sx={{ py: 6, textAlign: 'center', px: 3 }}>
                    <Typography variant="body2" sx={{ color: 'var(--color-text-muted)' }}>
                      No events scheduled in {MONTH_NAMES[currentMonth]} {currentYear}.
                    </Typography>
                  </Box>
                );
              }

              const todayStr = new Date().toDateString();
              return (
                <Stack spacing={0} sx={{ pb: 2 }}>
                  {daysWithEvents.map(({ date, events }) => {
                    const isToday = date.toDateString() === todayStr;
                    return (
                      <Box key={date.toISOString()} ref={isToday ? todayRowRef : undefined}>
                        {/* Day header */}
                        <Box
                          sx={{
                            position: 'sticky',
                            top: 0,
                            zIndex: 5,
                            display: 'flex',
                            alignItems: 'baseline',
                            gap: 1.25,
                            px: 2,
                            py: 1,
                            // Solid opaque backgrounds — sticky headers need
                            // opaque fills so scrolling content underneath
                            // doesn't bleed through. Today uses the accent
                            // colour so it's immediately recognisable.
                            bgcolor: isToday ? 'var(--color-accent)' : '#0A2540',
                            borderBottom: '1px solid var(--color-border-strong)',
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: 900,
                              color: '#fff',
                              fontSize: '1rem',
                            }}
                          >
                            {date.getDate()} {date.toLocaleString('en-US', { month: 'short' })}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 700,
                              color: isToday ? 'rgba(255,255,255,0.92)' : 'rgba(255,255,255,0.75)',
                              textTransform: 'uppercase',
                              letterSpacing: '0.06em',
                              fontSize: '0.7rem',
                            }}
                          >
                            {date.toLocaleString('en-US', { weekday: 'long' })}
                            {isToday && ' • Today'}
                          </Typography>
                        </Box>

                        {/* Event cards for this day */}
                        <Stack spacing={1} sx={{ px: 2, py: 1.25, bgcolor: 'var(--color-surface)' }}>
                          {events.map((evt) => {
                            const evtColor = evt.colorCode || 'var(--color-accent)';
                            const timeFormatted = formatCalendarEventTime(evt.start, evt.end);
                            const isMulti = isMultiDayEvent(evt.start, evt.end);
                            return (
                              <Box
                                key={evt.id}
                                onClick={() => setSelectedEvent(evt)}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1.25,
                                  p: 1.25,
                                  borderRadius: 1.5,
                                  bgcolor: 'var(--color-surface)',
                                  border: '1px solid var(--color-border-strong)',
                                  borderLeft: `4px solid ${evtColor}`,
                                  cursor: 'pointer',
                                  transition: 'border-color 120ms ease, box-shadow 120ms ease',
                                  '&:hover': {
                                    borderColor: 'var(--color-accent)',
                                    boxShadow: '0 0 0 1px var(--color-accent)',
                                  },
                                }}
                              >
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: 800,
                                      color: 'var(--color-text-strong)',
                                      lineHeight: 1.3,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      display: '-webkit-box',
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: 'vertical',
                                    }}
                                  >
                                    {evt.name}
                                  </Typography>
                                  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.4 }}>
                                    <AccessTimeIcon sx={{ fontSize: '0.85rem', color: 'var(--color-text-muted)' }} />
                                    <Typography
                                      variant="caption"
                                      sx={{ color: 'var(--color-text-muted)', fontWeight: 600, fontSize: '0.75rem' }}
                                    >
                                      {isMulti ? 'All day' : timeFormatted || 'Time TBD'}
                                    </Typography>
                                    {evt.location && (
                                      <>
                                        <Box sx={{ color: 'var(--color-text-muted)', fontSize: '0.75rem' }}>·</Box>
                                        <LocationOnIcon sx={{ fontSize: '0.85rem', color: 'var(--color-text-muted)' }} />
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            color: 'var(--color-text-muted)',
                                            fontWeight: 600,
                                            fontSize: '0.75rem',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            minWidth: 0,
                                            flex: 1,
                                          }}
                                          title={evt.location.replace(/<[^>]+>/g, '')}
                                        >
                                          {evt.location.replace(/<[^>]+>/g, '')}
                                        </Typography>
                                      </>
                                    )}
                                  </Stack>
                                </Box>
                                <ChevronRightIcon sx={{ color: 'var(--color-text-muted)', flexShrink: 0 }} />
                              </Box>
                            );
                          })}
                        </Stack>
                      </Box>
                    );
                  })}
                </Stack>
              );
            })()
          ) : (
          <Box sx={{ minWidth: { xs: '1100px', lg: '100%' }, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'contents' }}>
              
              {/* Day Headers row */}
              <Box
                sx={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 10,
                  display: 'grid',
                  gridTemplateColumns: 'repeat(7, 1fr)',
                  bgcolor: '#0A2540',
                  borderBottom: '1px solid var(--color-border-strong)',
                }}
              >
                {WEEK_DAYS.map((day) => (
                  <Box
                    key={day}
                    sx={{
                      py: 1.5,
                      textAlign: 'center',
                      color: '#fff',
                      fontWeight: 850,
                      fontSize: '0.8rem',
                      letterSpacing: '0.07em',
                    }}
                  >
                    {day}
                  </Box>
                ))}
              </Box>

              {/* Weekly Rows */}
              {weeks.map((week, wIdx) => {
                const packedWeekEvents = packEventsForWeek(week, activities);
                const todayStr = new Date().toDateString();
                const weekHasToday = week.some((d) => d.toDateString() === todayStr);
                return (
                  <React.Fragment key={wIdx}>
                    {/* Weekly Dates Subheader */}
                    <Box
                      ref={weekHasToday ? todayRowRef : undefined}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(7, 1fr)',
                        bgcolor: '#0A2540',
                        borderBottom: '1px solid var(--color-border-strong)',
                      }}
                    >
                      {week.map((date) => {
                        const isSelectedMonth = date.getMonth() === currentMonth;
                        return (
                          <Box
                            key={date.toISOString()}
                            sx={{
                              py: 0.75,
                              textAlign: 'center',
                              fontWeight: 800,
                              fontSize: '0.85rem',
                              color: isSelectedMonth ? '#fff' : 'rgba(255,255,255,0.55)',
                              opacity: isSelectedMonth ? 1 : 0.6,
                              borderRight: '1px solid var(--color-border-strong)',
                              '&:last-child': { borderRight: 'none' },
                            }}
                          >
                            {date.getDate()} {date.toLocaleString('en-US', { month: 'short' })}
                          </Box>
                        );
                      })}
                    </Box>

                    {/* Weekly Events Grid (Unified Blocks) */}
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(7, 1fr)',
                        borderBottom: '1px solid var(--color-border-strong)',
                        bgcolor: 'var(--color-surface)',
                        position: 'relative',
                        p: 1,
                      }}
                    >
                      {/* 1. Column Background Cells */}
                      {week.map((date, idx) => {
                        const isSelectedMonth = date.getMonth() === currentMonth;
                        return (
                          <Box
                            key={`bg-${date.toISOString()}`}
                            sx={{
                              gridColumn: idx + 1,
                              gridRow: '1 / 99', // Span all rows to cover all stacked blocks
                              minHeight: 140,
                              bgcolor: isSelectedMonth ? 'var(--color-surface)' : 'rgba(0,0,0,0.02)',
                              borderRight: '1px solid var(--color-border-strong)',
                              '&:last-child': { borderRight: 'none' },
                              zIndex: 1,
                            }}
                          />
                        );
                      })}

                      {/* 2. Event Blocks */}
                      {packedWeekEvents.map((packed) => {
                        const evt = packed.event;
                        const evtColor = evt.colorCode || 'var(--color-accent)';
                        const timeFormatted = formatCalendarEventTime(evt.start, evt.end);

                        return (
                          <Box
                            key={evt.id}
                            onClick={() => setSelectedEvent(evt)}
                            sx={{
                              gridColumn: `${packed.startCol + 1} / span ${packed.endCol - packed.startCol + 1}`,
                              gridRow: packed.gridRow,
                              zIndex: 2,
                              m: 0.5,
                              bgcolor: evtColor,
                              color: '#fff',
                              py: 0.85,
                              px: 1.5,
                              borderRadius: 1.5,
                              fontWeight: 800,
                              fontSize: '0.78rem',
                              cursor: 'pointer',
                              textAlign: 'center',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                              '&:hover': {
                                opacity: 0.9,
                                transform: 'translateY(-1px)',
                              },
                              transition: 'all 120ms ease-in-out',
                            }}
                            title={`${evt.name} (${timeFormatted})`}
                          >
                            <Box component="span" sx={{ fontWeight: 900, mr: 0.75, opacity: 0.9 }}>
                              {timeFormatted}
                            </Box>
                            {evt.name}
                          </Box>
                        );
                      })}
                    </Box>
                  </React.Fragment>
                );
              })}
            </Box>
          </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Selected Event Popup Dialog */}
      <Dialog
        open={Boolean(selectedEvent)}
        onClose={() => setSelectedEvent(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: 'hidden',
            bgcolor: 'var(--color-surface)',
          },
        }}
      >
        {selectedEvent && (
          <>
            {/* Banner Image */}
            <Box sx={{ position: 'relative', width: '100%', height: 240 }}>
              <Box
                component="img"
                src={selectedEvent.thumbnail || eventPlaceholderImg}
                alt={selectedEvent.name}
                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
              <IconButton
                onClick={() => setSelectedEvent(null)}
                sx={{
                  position: 'absolute',
                  top: 16,
                  right: 16,
                  bgcolor: 'rgba(0, 0, 0, 0.5)',
                  color: '#fff',
                  '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.7)' },
                  zIndex: 10,
                }}
                size="small"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            <DialogContent sx={{ p: 3 }}>
              <Stack spacing={2.5}>
                {/* Title */}
                <Typography variant="h5" sx={{ fontWeight: 850, color: 'var(--color-text-strong)', lineHeight: 1.3 }}>
                  {selectedEvent.name}
                </Typography>

                {/* Quick Metadata */}
                <Stack spacing={1.25} sx={{ p: 2, bgcolor: 'var(--color-surface-elevated)', borderRadius: 2, border: '1px solid var(--color-border-strong)' }}>
                  {/* Date */}
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <CalendarMonthIcon sx={{ color: 'var(--color-accent)', fontSize: '1.2rem' }} />
                    <Typography variant="body2" sx={{ fontWeight: 700, color: 'var(--color-text-strong)' }}>
                      {formatEventDateTime(selectedEvent.start, selectedEvent.end).dateStr}
                    </Typography>
                  </Stack>

                  {/* Time */}
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <AccessTimeIcon sx={{ color: 'var(--color-accent)', fontSize: '1.2rem' }} />
                    <Typography variant="body2" sx={{ fontWeight: 700, color: 'var(--color-text-strong)' }}>
                      {formatEventDateTime(selectedEvent.start, selectedEvent.end).timeStr}
                    </Typography>
                  </Stack>

                  {/* Location */}
                  {selectedEvent.location && (
                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                      <LocationOnIcon sx={{ color: 'var(--color-accent)', fontSize: '1.2rem', mt: 0.25 }} />
                      <Typography
                        variant="body2"
                        component="div"
                        dangerouslySetInnerHTML={{ __html: formatHtmlDescription(selectedEvent.location) }}
                        sx={{
                          fontWeight: 700,
                          color: 'var(--color-text-strong)',
                          '& a': {
                            color: 'var(--color-accent)',
                            fontWeight: 800,
                            textDecoration: 'underline',
                          },
                        }}
                      />
                    </Stack>
                  )}
                </Stack>

                {/* Description */}
                {selectedEvent.description && (
                  <Stack spacing={1}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'var(--color-text-strong)' }}>
                      Description
                    </Typography>
                    <Typography
                      variant="body1"
                      component="div"
                      dangerouslySetInnerHTML={{ __html: formatHtmlDescription(selectedEvent.description) }}
                      sx={{
                        color: 'var(--color-text-muted)',
                        fontSize: '0.95rem',
                        lineHeight: 1.6,
                        '& a': {
                          color: 'var(--color-accent)',
                          fontWeight: 800,
                          textDecoration: 'underline',
                        },
                      }}
                    />
                  </Stack>
                )}

                {/* Footer Action Buttons */}
                <Box
                  sx={{
                    pt: 2.5,
                    borderTop: '1px solid var(--color-border-strong)',
                    display: 'flex',
                    gap: 1.5,
                    justifyContent: 'flex-end',
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={() => setSelectedEvent(null)}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 700,
                      borderRadius: 999,
                      color: 'var(--color-text-strong)',
                      borderColor: 'var(--color-border-strong)',
                      px: 3,
                      '&:hover': {
                        borderColor: 'var(--color-accent)',
                        bgcolor: 'var(--color-accent-soft)',
                      },
                    }}
                  >
                    Close
                  </Button>
                  <Button
                    component="a"
                    href={buildGoogleCalendarUrl(selectedEvent)}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="contained"
                    startIcon={<CalendarMonthIcon />}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 700,
                      borderRadius: 999,
                      bgcolor: 'var(--color-accent)',
                      color: '#fff',
                      px: 3,
                      '&:hover': {
                        bgcolor: 'var(--color-accent-strong)',
                      },
                    }}
                  >
                    Add to Calendar
                  </Button>
                </Box>
              </Stack>
            </DialogContent>
          </>
        )}
      </Dialog>

      <ProjectFiltersDialog
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        value={filters}
        onApply={(next) => {
          navigate(buildPeSearchPageHref(search.trim(), next));
        }}
      />
    </Stack>
  );
}
